import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import type { Lead } from '@azoupvendas/shared';

interface LeadCardProps {
  lead: Lead;
  onClick: (lead: Lead) => void;
}

const formatCurrency = (value?: number) =>
  value != null
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
    : null;

// AIDEV-NOTE: Componente draggable via @dnd-kit/sortable — requer DndContext + SortableContext ancestrais
// AIDEV-NOTE: listeners do dnd-kit ficam em um drag-handle dedicado para não bloquear onClick do conteúdo
export function LeadCard({ lead, onClick }: LeadCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lead.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const formattedValue = formatCurrency(lead.valor_estimado);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        mb: 1,
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        '&:hover': { boxShadow: 3 },
        userSelect: 'none'
      }}
    >
      <CardContent
        onClick={() => onClick(lead)}
        sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, cursor: 'pointer' }}
      >
        <Box display="flex" alignItems="flex-start" gap={0.5}>
          {/* drag handle — listeners isolados do conteúdo clicável */}
          <Box
            {...listeners}
            sx={{ cursor: 'grab', color: 'text.disabled', mt: '2px', flexShrink: 0 }}
            aria-label="arrastar"
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {lead.nome}
            </Typography>
            {formattedValue && (
              <Typography variant="caption" color="text.secondary">
                {formattedValue}
              </Typography>
            )}
            {lead.proximo_contato_em && (
              <Box mt={0.5}>
                <Chip
                  label={`📞 ${new Date(lead.proximo_contato_em).toLocaleDateString('pt-BR')}`}
                  size="small"
                  sx={{ fontSize: '10px', height: 18 }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
